import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { 
  Trophy, Vote, Users, Star, LogOut, Home, User, 
  Flame, Target, Crown, Medal, Shield, Award, 
  UserPlus, UserCheck, UserX, Heart, Camera, Check,
  Bell, Settings, Search, MessageCircle, X, ChevronRight,
  Image, Video, Clock, Plus, Trash2, Ruler, Weight, Upload, Link,
  Calendar, ImageIcon
} from "lucide-react";
import { toast, Toaster } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

const CATEGORIAS_ICONS = {
  esforco: Flame,
  tecnica: Target,
  equipe: Users,
  evolucao: Star,
  disciplina: Award,
  lideranca: Crown,
  fair_play: Medal,
  goleador: Trophy,
  defesa: Shield,
  criatividade: Star
};

const POSICOES_FUTEBOL = [
  { id: "goleiro", nome: "Goleiro" },
  { id: "zagueiro", nome: "Zagueiro" },
  { id: "lateral_direito", nome: "Lateral Direito" },
  { id: "lateral_esquerdo", nome: "Lateral Esquerdo" },
  { id: "volante", nome: "Volante" },
  { id: "meia", nome: "Meia" },
  { id: "meia_atacante", nome: "Meia Atacante" },
  { id: "ponta_direita", nome: "Ponta Direita" },
  { id: "ponta_esquerda", nome: "Ponta Esquerda" },
  { id: "centroavante", nome: "Centroavante" },
  { id: "atacante", nome: "Atacante" }
];

const PES_DOMINANTES = ["Direito", "Esquerdo", "Ambidestro"];

export default function ArenaAluno() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("inicio");
  
  // Dados do aluno logado
  const [alunoData, setAlunoData] = useState(null);
  const [token, setToken] = useState(null);
  const [perfil, setPerfil] = useState(null);
  const [estatisticas, setEstatisticas] = useState(null);
  
  // Avatares e Perfil
  const [avatares, setAvatares] = useState([]);
  const [showAvatarDialog, setShowAvatarDialog] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [bio, setBio] = useState("");
  
  // Perfil completo
  const [posicao, setPosicao] = useState("");
  const [peDominante, setPeDominante] = useState("");
  const [altura, setAltura] = useState("");
  const [peso, setPeso] = useState("");
  const [numeroCamisa, setNumeroCamisa] = useState("");
  const [dataNascimento, setDataNascimento] = useState("");
  
  // Galeria de mídia
  const [showGaleriaDialog, setShowGaleriaDialog] = useState(false);
  const [novaFotoUrl, setNovaFotoUrl] = useState("");
  const [novaFotoLegenda, setNovaFotoLegenda] = useState("");
  const [novoVideoUrl, setNovoVideoUrl] = useState("");
  const [novoVideoLegenda, setNovoVideoLegenda] = useState("");
  
  // Notificações
  const [notificacoes, setNotificacoes] = useState([]);
  const [notificacoesNaoLidas, setNotificacoesNaoLidas] = useState(0);
  
  // Colegas e Amizades
  const [colegas, setColegas] = useState([]);
  const [amigos, setAmigos] = useState([]);
  const [convitesPendentes, setConvitesPendentes] = useState([]);
  const [searchColega, setSearchColega] = useState("");
  
  // Perfil de outro aluno
  const [perfilOutro, setPerfilOutro] = useState(null);
  const [showPerfilDialog, setShowPerfilDialog] = useState(false);
  
  // Votações
  const [votacoes, setVotacoes] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [votacaoAtual, setVotacaoAtual] = useState(null);
  const [votosAluno, setVotosAluno] = useState({});
  const [alunosTurma, setAlunosTurma] = useState([]);
  
  // Hall da Fama
  const [hallDaFama, setHallDaFama] = useState(null);

  useEffect(() => {
    const savedToken = localStorage.getItem('arena_aluno_token');
    const savedAluno = localStorage.getItem('arena_aluno_data');
    
    if (savedToken && savedAluno) {
      setToken(savedToken);
      setAlunoData(JSON.parse(savedAluno));
      fetchMeuPerfil(savedToken);
      fetchAvatares();
      fetchCategorias();
    } else {
      navigate('/login');
    }
  }, []);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  const fetchMeuPerfil = async (authToken) => {
    try {
      const response = await axios.get(`${API}/arena/aluno/perfil`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setPerfil(response.data.perfil);
      setEstatisticas(response.data.estatisticas);
      setAlunoData(prev => ({ ...prev, ...response.data.aluno }));
      setBio(response.data.perfil.bio || "");
      // Carregar campos do perfil completo
      setPosicao(response.data.perfil.posicao || "");
      setPeDominante(response.data.perfil.pe_dominante || "");
      setAltura(response.data.perfil.altura || "");
      setPeso(response.data.perfil.peso || "");
      setNumeroCamisa(response.data.perfil.numero_camisa || "");
      setDataNascimento(response.data.perfil.data_nascimento || "");
      setLoading(false);
      
      // Carregar outros dados
      fetchColegas(authToken);
      fetchAmigos(authToken);
      fetchConvites(authToken);
      fetchVotacoes(authToken);
      fetchHallDaFama(authToken);
      fetchNotificacoes(authToken);
    } catch (error) {
      console.error("Erro ao buscar perfil:", error);
      if (error.response?.status === 401) {
        logout();
      }
      setLoading(false);
    }
  };

  const fetchNotificacoes = async (authToken) => {
    try {
      const response = await axios.get(`${API}/arena/aluno/notificacoes`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setNotificacoes(response.data.notificacoes || []);
      setNotificacoesNaoLidas(response.data.nao_lidas || 0);
    } catch (error) {
      console.error("Erro ao buscar notificações");
    }
  };

  const marcarNotificacoesLidas = async () => {
    try {
      await axios.post(`${API}/arena/aluno/notificacoes/marcar-lidas`, {}, getAuthHeaders());
      setNotificacoesNaoLidas(0);
      setNotificacoes(prev => prev.map(n => ({ ...n, lida: true })));
    } catch (error) {
      console.error("Erro ao marcar notificações");
    }
  };

  const fetchAvatares = async () => {
    try {
      const response = await axios.get(`${API}/arena/avatares`);
      setAvatares(response.data);
    } catch (error) {
      console.error("Erro ao buscar avatares");
    }
  };

  const fetchColegas = async (authToken) => {
    try {
      const response = await axios.get(`${API}/arena/aluno/colegas`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setColegas(response.data);
    } catch (error) {
      console.error("Erro ao buscar colegas");
    }
  };

  const fetchAmigos = async (authToken) => {
    try {
      const response = await axios.get(`${API}/arena/aluno/amigos`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setAmigos(response.data);
    } catch (error) {
      console.error("Erro ao buscar amigos");
    }
  };

  const fetchConvites = async (authToken) => {
    try {
      const response = await axios.get(`${API}/arena/aluno/convites`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setConvitesPendentes(response.data);
    } catch (error) {
      console.error("Erro ao buscar convites");
    }
  };

  const fetchVotacoes = async (authToken) => {
    try {
      const response = await axios.get(`${API}/arena/aluno/votacoes`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setVotacoes(response.data.sessoes || []);
    } catch (error) {
      console.error("Erro ao buscar votações");
    }
  };

  const fetchCategorias = async () => {
    try {
      const response = await axios.get(`${API}/arena/categorias`);
      setCategorias(response.data);
    } catch (error) {
      console.error("Erro ao buscar categorias");
    }
  };

  const fetchHallDaFama = async (authToken) => {
    try {
      const response = await axios.get(`${API}/arena/aluno/hall-da-fama`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setHallDaFama(response.data);
    } catch (error) {
      console.error("Erro ao buscar hall da fama");
    }
  };

  const logout = () => {
    localStorage.removeItem('arena_aluno_token');
    localStorage.removeItem('arena_aluno_data');
    navigate('/login');
  };

  // ========== PERFIL ==========
  
  const salvarPerfil = async () => {
    setLoading(true);
    try {
      const updateData = {
        bio: bio,
        avatar_tipo: selectedAvatar ? "predefinido" : perfil?.avatar_tipo,
        avatar_id: selectedAvatar || perfil?.avatar_id,
        posicao: posicao || null,
        pe_dominante: peDominante || null,
        altura: altura ? parseFloat(altura) : null,
        peso: peso ? parseFloat(peso) : null,
        numero_camisa: numeroCamisa ? parseInt(numeroCamisa) : null,
        data_nascimento: dataNascimento || null
      };
      
      await axios.put(`${API}/arena/aluno/perfil`, updateData, getAuthHeaders());
      toast.success("Perfil atualizado!");
      fetchMeuPerfil(token);
      setShowAvatarDialog(false);
    } catch (error) {
      toast.error("Erro ao salvar perfil");
    } finally {
      setLoading(false);
    }
  };
  
  // Calcular idade a partir da data de nascimento
  const calcularIdade = (dataNasc) => {
    if (!dataNasc) return null;
    const hoje = new Date();
    const nascimento = new Date(dataNasc);
    let idade = hoje.getFullYear() - nascimento.getFullYear();
    const mes = hoje.getMonth() - nascimento.getMonth();
    if (mes < 0 || (mes === 0 && hoje.getDate() < nascimento.getDate())) {
      idade--;
    }
    return idade;
  };

  // ========== GALERIA ==========
  
  // Estado para upload de arquivos
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  
  const handleFileUpload = async (event, tipo) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const isUploading = tipo === 'foto' ? setUploadingFoto : setUploadingVideo;
    isUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tipo', tipo);
      formData.append('legenda', '');
      
      const response = await axios.post(
        `${API}/arena/aluno/galeria/upload`,
        formData,
        {
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success(`${tipo === 'foto' ? 'Foto' : 'Vídeo'} enviada com sucesso!`);
      fetchMeuPerfil(token);
      setShowGaleriaDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Erro ao enviar ${tipo}`);
    } finally {
      isUploading(false);
    }
  };
  
  const adicionarFoto = async () => {
    if (!novaFotoUrl) {
      toast.error("Cole o link da foto");
      return;
    }
    try {
      await axios.post(`${API}/arena/aluno/galeria/adicionar`, {
        url: novaFotoUrl,
        legenda: novaFotoLegenda,
        tipo: "foto"
      }, getAuthHeaders());
      toast.success("Foto adicionada!");
      setNovaFotoUrl("");
      setNovaFotoLegenda("");
      fetchMeuPerfil(token);
      setShowGaleriaDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao adicionar foto");
    }
  };

  const adicionarVideo = async () => {
    if (!novoVideoUrl) {
      toast.error("Cole o link do vídeo");
      return;
    }
    try {
      await axios.post(`${API}/arena/aluno/galeria/adicionar`, {
        url: novoVideoUrl,
        legenda: novoVideoLegenda,
        tipo: "video"
      }, getAuthHeaders());
      toast.success("Vídeo adicionado!");
      setNovoVideoUrl("");
      setNovoVideoLegenda("");
      fetchMeuPerfil(token);
      setShowGaleriaDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao adicionar vídeo");
    }
  };

  const removerMidia = async (tipo, id) => {
    try {
      await axios.delete(`${API}/arena/aluno/galeria/${tipo}/${id}`, getAuthHeaders());
      toast.success(`${tipo === 'foto' ? 'Foto' : 'Vídeo'} removida!`);
      fetchMeuPerfil(token);
    } catch (error) {
      toast.error("Erro ao remover mídia");
    }
  };

  // ========== AMIZADES ==========

  const enviarConvite = async (destinatarioId) => {
    try {
      await axios.post(`${API}/arena/aluno/amizade/enviar`, 
        { destinatario_id: destinatarioId }, 
        getAuthHeaders()
      );
      toast.success("Convite enviado!");
      fetchColegas(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao enviar convite");
    }
  };

  const aceitarConvite = async (amizadeId) => {
    try {
      await axios.post(`${API}/arena/aluno/amizade/${amizadeId}/aceitar`, {}, getAuthHeaders());
      toast.success("Amizade aceita!");
      fetchConvites(token);
      fetchAmigos(token);
      fetchColegas(token);
    } catch (error) {
      toast.error("Erro ao aceitar convite");
    }
  };

  const recusarConvite = async (amizadeId) => {
    try {
      await axios.post(`${API}/arena/aluno/amizade/${amizadeId}/recusar`, {}, getAuthHeaders());
      toast.info("Convite recusado");
      fetchConvites(token);
      fetchColegas(token);
    } catch (error) {
      toast.error("Erro ao recusar convite");
    }
  };

  const desfazerAmizade = async (amizadeId) => {
    try {
      await axios.delete(`${API}/arena/aluno/amizade/${amizadeId}`, getAuthHeaders());
      toast.info("Amizade desfeita");
      fetchAmigos(token);
      fetchColegas(token);
    } catch (error) {
      toast.error("Erro ao desfazer amizade");
    }
  };

  const verPerfil = async (alunoId) => {
    try {
      const response = await axios.get(`${API}/arena/aluno/perfil/${alunoId}`, getAuthHeaders());
      setPerfilOutro(response.data);
      setShowPerfilDialog(true);
    } catch (error) {
      toast.error("Erro ao carregar perfil");
    }
  };

  // ========== VOTAÇÃO ==========

  const iniciarVotacao = async (sessao) => {
    try {
      const response = await axios.get(`${API}/arena/votacao/${sessao.id}/alunos`, getAuthHeaders());
      setAlunosTurma(response.data);
      setVotacaoAtual(sessao);
      setVotosAluno({});
    } catch (error) {
      toast.error("Erro ao carregar alunos");
    }
  };

  const registrarVoto = async () => {
    if (Object.keys(votosAluno).length !== votacaoAtual.categorias.length) {
      toast.error("Vote em todas as categorias!");
      return;
    }

    setLoading(true);
    try {
      for (const [categoriaId, votadoId] of Object.entries(votosAluno)) {
        await axios.post(`${API}/arena/aluno/votar`, {
          sessao_id: votacaoAtual.id,
          categoria_id: categoriaId,
          votado_id: votadoId
        }, getAuthHeaders());
      }
      toast.success("Votos registrados!");
      setVotacaoAtual(null);
      fetchVotacoes(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao votar");
    } finally {
      setLoading(false);
    }
  };

  // Filtrar colegas pela busca
  const colegasFiltrados = colegas.filter(c => 
    c.nome.toLowerCase().includes(searchColega.toLowerCase())
  );

  // ========== RENDER ==========

  if (loading && !perfil) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-pink-800 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Toaster position="top-center" richColors />
      
      {/* Header - Mobile Friendly */}
      <header className="bg-black/30 backdrop-blur-lg border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <Avatar className="h-8 w-8 sm:h-10 sm:w-10 border-2 border-purple-400">
              <AvatarImage src={perfil?.avatar_url} />
              <AvatarFallback className="bg-purple-600 text-white text-sm">
                {alunoData?.nome?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="text-white font-semibold text-xs sm:text-sm truncate max-w-[120px] sm:max-w-none">{alunoData?.nome}</p>
              <p className="text-purple-300 text-[10px] sm:text-xs truncate max-w-[120px] sm:max-w-none">{alunoData?.escola}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Notificações */}
            {(notificacoesNaoLidas > 0 || convitesPendentes.length > 0) && (
              <Button 
                variant="ghost" 
                size="icon"
                className="relative text-white hover:bg-white/10 h-8 w-8 sm:h-9 sm:w-9"
                onClick={() => {
                  setActiveTab("inicio");
                  marcarNotificacoesLidas();
                }}
              >
                <Bell className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center">
                  {notificacoesNaoLidas + convitesPendentes.length}
                </span>
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="icon"
              className="text-white hover:bg-white/10 h-8 w-8 sm:h-9 sm:w-9"
              onClick={logout}
            >
              <LogOut className="h-4 w-4 sm:h-5 sm:w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content - Mobile Friendly */}
      <main className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full flex bg-black/30 border border-white/10 mb-4 sm:mb-6 rounded-lg p-1">
            <TabsTrigger value="inicio" className="flex-1 text-white data-[state=active]:bg-purple-600 py-2 px-1 sm:px-3 text-xs sm:text-sm rounded-md flex items-center justify-center gap-1">
              <Home className="h-4 w-4" />
              <span className="hidden sm:inline">Início</span>
            </TabsTrigger>
            <TabsTrigger value="perfil" className="flex-1 text-white data-[state=active]:bg-purple-600 py-2 px-1 sm:px-3 text-xs sm:text-sm rounded-md flex items-center justify-center gap-1">
              <User className="h-4 w-4" />
              <span className="hidden sm:inline">Perfil</span>
            </TabsTrigger>
            <TabsTrigger value="galeria" className="flex-1 text-white data-[state=active]:bg-purple-600 py-2 px-1 sm:px-3 text-xs sm:text-sm rounded-md flex items-center justify-center gap-1 relative">
              <ImageIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Galeria</span>
              {(perfil?.fotos?.length > 0 || perfil?.videos?.length > 0) && (
                <span className="absolute -top-1 -right-1 bg-pink-500 text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center">
                  {(perfil?.fotos?.length || 0) + (perfil?.videos?.length || 0)}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="amigos" className="flex-1 text-white data-[state=active]:bg-purple-600 relative py-2 px-1 sm:px-3 text-xs sm:text-sm rounded-md flex items-center justify-center gap-1">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Amigos</span>
              {convitesPendentes.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center">
                  {convitesPendentes.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="arena" className="flex-1 text-white data-[state=active]:bg-purple-600 py-2 px-1 sm:px-3 text-xs sm:text-sm rounded-md flex items-center justify-center gap-1">
              <Trophy className="h-4 w-4" />
              <span className="hidden sm:inline">Arena</span>
            </TabsTrigger>
          </TabsList>

          {/* ========== TAB: INÍCIO ========== */}
          <TabsContent value="inicio" className="space-y-4 sm:space-y-6">
            {/* Card de Perfil Resumido */}
            <Card className="bg-gradient-to-r from-purple-600 to-pink-600 border-0 text-white overflow-hidden">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-start gap-3 sm:gap-4">
                  <Avatar className="h-16 w-16 sm:h-20 sm:w-20 border-4 border-white/30 flex-shrink-0">
                    <AvatarImage src={perfil?.avatar_url} />
                    <AvatarFallback className="bg-white/20 text-white text-xl">
                      {alunoData?.nome?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg sm:text-2xl font-bold truncate">{alunoData?.nome}</h2>
                    <p className="text-white/70 text-sm">{alunoData?.escola}</p>
                    {perfil?.posicao && (
                      <Badge className="bg-white/20 mt-1 text-xs">
                        {POSICOES_FUTEBOL.find(p => p.id === perfil.posicao)?.nome || perfil.posicao}
                      </Badge>
                    )}
                  </div>
                  {perfil?.numero_camisa && (
                    <div className="text-right flex-shrink-0">
                      <span className="text-3xl sm:text-4xl font-bold text-white/90">#{perfil.numero_camisa}</span>
                    </div>
                  )}
                </div>
                
                {/* Dados do Atleta */}
                <div className="flex flex-wrap gap-3 sm:gap-4 mt-4 pt-4 border-t border-white/20">
                  {perfil?.data_nascimento && (
                    <div className="flex items-center gap-1.5 text-white/80">
                      <Calendar className="h-4 w-4" />
                      <span className="text-sm">{calcularIdade(perfil.data_nascimento)} anos</span>
                    </div>
                  )}
                  {perfil?.pe_dominante && (
                    <div className="flex items-center gap-1.5 text-white/80">
                      <span className="text-sm">Pé {perfil.pe_dominante}</span>
                    </div>
                  )}
                  {perfil?.altura && (
                    <div className="flex items-center gap-1.5 text-white/80">
                      <Ruler className="h-4 w-4" />
                      <span className="text-sm">{perfil.altura}m</span>
                    </div>
                  )}
                  {perfil?.peso && (
                    <div className="flex items-center gap-1.5 text-white/80">
                      <Weight className="h-4 w-4" />
                      <span className="text-sm">{perfil.peso}kg</span>
                    </div>
                  )}
                </div>
                
                {perfil?.bio && (
                  <p className="text-white/70 text-sm mt-3 italic">"{perfil.bio}"</p>
                )}
              </CardContent>
            </Card>

            {/* Estatísticas Rápidas */}
            <div className="grid grid-cols-4 gap-2 sm:gap-4">
              <Card className="bg-black/30 border-white/10 text-center">
                <CardContent className="p-2 sm:p-4">
                  <Trophy className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-400 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-white">{estatisticas?.pontos || 0}</p>
                  <p className="text-[10px] sm:text-xs text-gray-400">Pontos</p>
                </CardContent>
              </Card>
              <Card className="bg-black/30 border-white/10 text-center">
                <CardContent className="p-2 sm:p-4">
                  <Medal className="h-6 w-6 sm:h-8 sm:w-8 text-orange-400 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-white">{estatisticas?.vitorias || 0}</p>
                  <p className="text-[10px] sm:text-xs text-gray-400">Vitórias</p>
                </CardContent>
              </Card>
              <Card className="bg-black/30 border-white/10 text-center">
                <CardContent className="p-2 sm:p-4">
                  <Vote className="h-6 w-6 sm:h-8 sm:w-8 text-purple-400 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-white">{estatisticas?.votos_recebidos || 0}</p>
                  <p className="text-[10px] sm:text-xs text-gray-400">Votos</p>
                </CardContent>
              </Card>
              <Card className="bg-black/30 border-white/10 text-center">
                <CardContent className="p-2 sm:p-4">
                  <Users className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-white">{amigos.length}</p>
                  <p className="text-[10px] sm:text-xs text-gray-400">Amigos</p>
                </CardContent>
              </Card>
            </div>

            {/* Convites Pendentes */}
            {convitesPendentes.length > 0 && (
              <Card className="bg-black/30 border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
                    <Bell className="h-5 w-5 text-yellow-400" />
                    Convites de Amizade
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {convitesPendentes.slice(0, 3).map((convite) => (
                    <div key={convite.convite_id} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={convite.aluno.avatar_url} />
                          <AvatarFallback className="bg-purple-600 text-white">
                            {convite.aluno.nome?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-white font-medium text-sm">{convite.aluno.nome}</span>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700 h-8 w-8 p-0"
                          onClick={() => aceitarConvite(convite.convite_id)}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="border-red-500 text-red-500 hover:bg-red-500/20 h-8 w-8 p-0"
                          onClick={() => recusarConvite(convite.convite_id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Votações Abertas */}
            {votacoes.length > 0 && (
              <Card className="bg-gradient-to-r from-purple-600/30 to-pink-600/30 border-purple-500/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
                    <Vote className="h-5 w-5 text-purple-400" />
                    Votações Abertas
                    <Badge className="bg-green-500 ml-2">{votacoes.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {votacoes.map((sessao) => (
                    <div 
                      key={sessao.id} 
                      className="bg-white/10 rounded-lg p-4 cursor-pointer hover:bg-white/15 transition-colors border border-white/10"
                      onClick={() => {
                        setActiveTab("arena");
                        iniciarVotacao(sessao);
                      }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-white font-semibold">{sessao.turma_nome}</p>
                        {sessao.ja_votou ? (
                          <Badge className="bg-green-600">Votado</Badge>
                        ) : (
                          <Badge className="bg-yellow-600 animate-pulse">Votar</Badge>
                        )}
                      </div>
                      <p className="text-gray-300 text-sm">Treino: {sessao.data_treino}</p>
                      {sessao.tempo_restante_horas && (
                        <div className="flex items-center gap-2 mt-2 text-orange-400">
                          <Clock className="h-4 w-4" />
                          <span className="text-sm">
                            {sessao.tempo_restante_horas > 1 
                              ? `${Math.floor(sessao.tempo_restante_horas)}h restantes`
                              : `${Math.round(sessao.tempo_restante_horas * 60)}min restantes`
                            }
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-purple-300 text-xs">
                          {sessao.categorias?.length || 0} categorias
                        </span>
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Hall da Fama */}
            {hallDaFama && hallDaFama.top_mes?.length > 0 && (
              <Card className="bg-gradient-to-br from-amber-500/20 to-yellow-500/20 border-amber-500/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Crown className="h-5 w-5 text-amber-400" />
                    Hall da Fama - {hallDaFama.mes}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {hallDaFama.top_mes?.map((destaque, index) => (
                      <div 
                        key={destaque.aluno_id}
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          destaque.sou_eu 
                            ? 'bg-purple-500/30 border border-purple-400' 
                            : 'bg-white/5'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-lg ${
                            index === 0 ? 'bg-amber-500' :
                            index === 1 ? 'bg-gray-400' :
                            index === 2 ? 'bg-orange-600' :
                            'bg-gray-600'
                          }`}>
                            {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                          </div>
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={destaque.avatar_url} />
                            <AvatarFallback className="bg-purple-600 text-white">
                              {destaque.aluno_nome?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className={`font-medium ${destaque.sou_eu ? 'text-purple-300' : 'text-white'}`}>
                              {destaque.aluno_nome} {destaque.sou_eu && '(você)'}
                            </p>
                            <p className="text-xs text-gray-400">{destaque.vitorias} vitórias</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {hallDaFama.minha_posicao && !hallDaFama.top_mes?.some(d => d.sou_eu) && (
                    <div className="mt-4 p-3 bg-purple-500/20 rounded-lg border border-purple-400/50">
                      <p className="text-purple-300 text-sm">
                        Sua posição: <span className="font-bold">{hallDaFama.minha_posicao.posicao}º</span> lugar
                        com {hallDaFama.minha_posicao.vitorias} vitórias
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ========== TAB: PERFIL ========== */}
          <TabsContent value="perfil" className="space-y-6">
            <Card className="bg-black/30 border-white/10">
              <CardContent className="p-6">
                {/* Avatar e Nome */}
                <div className="flex flex-col items-center text-center mb-6">
                  <div className="relative">
                    <Avatar className="h-24 w-24 border-4 border-purple-400">
                      <AvatarImage src={perfil?.avatar_url} />
                      <AvatarFallback className="bg-purple-600 text-white text-3xl">
                        {alunoData?.nome?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <Button 
                      size="icon"
                      className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full bg-purple-600 hover:bg-purple-700"
                      onClick={() => setShowAvatarDialog(true)}
                    >
                      <Camera className="h-4 w-4" />
                    </Button>
                  </div>
                  <h2 className="text-xl font-bold text-white mt-4">{alunoData?.nome}</h2>
                  <p className="text-gray-400">{alunoData?.escola}</p>
                  {perfil?.posicao && (
                    <Badge className="mt-2 bg-green-600">
                      {POSICOES_FUTEBOL.find(p => p.id === perfil.posicao)?.nome || perfil.posicao}
                    </Badge>
                  )}
                  {perfil?.bio && (
                    <p className="text-gray-300 mt-2 italic">"{perfil.bio}"</p>
                  )}
                </div>

                {/* Estatísticas */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{estatisticas?.pontos || 0}</p>
                    <p className="text-xs text-gray-400">Pontos</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{estatisticas?.vitorias || 0}</p>
                    <p className="text-xs text-gray-400">Vitórias</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{estatisticas?.votos_recebidos || 0}</p>
                    <p className="text-xs text-gray-400">Votos</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{amigos.length}</p>
                    <p className="text-xs text-gray-400">Amigos</p>
                  </div>
                </div>

                {/* Informações de Atleta */}
                <div className="grid grid-cols-2 gap-3 mb-6 p-4 bg-white/5 rounded-lg">
                  {perfil?.numero_camisa && (
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-bold text-purple-400">#{perfil.numero_camisa}</span>
                      <span className="text-gray-400 text-sm">Camisa</span>
                    </div>
                  )}
                  {perfil?.pe_dominante && (
                    <div className="flex items-center gap-2">
                      <span className="text-white">{perfil.pe_dominante}</span>
                      <span className="text-gray-400 text-sm">Pé</span>
                    </div>
                  )}
                  {perfil?.altura && (
                    <div className="flex items-center gap-2">
                      <Ruler className="h-4 w-4 text-blue-400" />
                      <span className="text-white">{perfil.altura}m</span>
                    </div>
                  )}
                  {perfil?.peso && (
                    <div className="flex items-center gap-2">
                      <Weight className="h-4 w-4 text-green-400" />
                      <span className="text-white">{perfil.peso}kg</span>
                    </div>
                  )}
                </div>

                {/* Medalhas */}
                {estatisticas?.medalhas?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-white font-semibold mb-2">Medalhas</h3>
                    <div className="flex flex-wrap gap-2">
                      {estatisticas.medalhas.map((medalha, idx) => (
                        <Badge key={idx} className="bg-yellow-500/20 text-yellow-400 border-yellow-500">
                          <Medal className="h-3 w-3 mr-1" />
                          {medalha}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bio */}
                <div className="space-y-3 sm:space-y-4 border-t border-white/10 pt-4">
                  <h3 className="text-white font-semibold text-sm sm:text-base">Editar Perfil</h3>
                  
                  <div className="space-y-2">
                    <Label className="text-gray-400 text-xs sm:text-sm">Bio</Label>
                    <Textarea
                      value={bio}
                      onChange={(e) => setBio(e.target.value)}
                      placeholder="Escreva algo sobre você..."
                      className="bg-white/10 border-white/20 text-white text-sm"
                      maxLength={150}
                      rows={2}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    <div className="space-y-1 sm:space-y-2">
                      <Label className="text-gray-400 text-xs sm:text-sm">Posição</Label>
                      <Select value={posicao} onValueChange={setPosicao}>
                        <SelectTrigger className="bg-white/10 border-white/20 text-white h-9 text-xs sm:text-sm">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          {POSICOES_FUTEBOL.map((p) => (
                            <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-1 sm:space-y-2">
                      <Label className="text-gray-400 text-xs sm:text-sm">Pé Dominante</Label>
                      <Select value={peDominante} onValueChange={setPeDominante}>
                        <SelectTrigger className="bg-white/10 border-white/20 text-white h-9 text-xs sm:text-sm">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          {PES_DOMINANTES.map((pe) => (
                            <SelectItem key={pe} value={pe}>{pe}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2 sm:gap-3">
                    <div className="space-y-1 sm:space-y-2">
                      <Label className="text-gray-400 text-xs sm:text-sm">Camisa</Label>
                      <Input
                        type="number"
                        value={numeroCamisa}
                        onChange={(e) => setNumeroCamisa(e.target.value)}
                        placeholder="Nº"
                        className="bg-white/10 border-white/20 text-white h-9 text-sm"
                        min={1}
                        max={99}
                      />
                    </div>
                    <div className="space-y-1 sm:space-y-2">
                      <Label className="text-gray-400 text-xs sm:text-sm">Altura</Label>
                      <Input
                        type="number"
                        value={altura}
                        onChange={(e) => setAltura(e.target.value)}
                        placeholder="1.70"
                        className="bg-white/10 border-white/20 text-white h-9 text-sm"
                        step={0.01}
                        min={0.5}
                        max={2.5}
                      />
                    </div>
                    <div className="space-y-1 sm:space-y-2">
                      <Label className="text-gray-400 text-xs sm:text-sm">Peso</Label>
                      <Input
                        type="number"
                        value={peso}
                        onChange={(e) => setPeso(e.target.value)}
                        placeholder="70"
                        className="bg-white/10 border-white/20 text-white h-9 text-sm"
                        min={20}
                        max={200}
                      />
                    </div>
                  </div>
                  
                  {/* Data de Nascimento */}
                  <div className="space-y-1 sm:space-y-2">
                    <Label className="text-gray-400 text-xs sm:text-sm">Data de Nascimento</Label>
                    <Input
                      type="date"
                      value={dataNascimento}
                      onChange={(e) => setDataNascimento(e.target.value)}
                      className="bg-white/10 border-white/20 text-white h-9 text-sm"
                    />
                  </div>
                  
                  <Button 
                    onClick={salvarPerfil} 
                    className="w-full bg-purple-600 hover:bg-purple-700 h-10 sm:h-11"
                    disabled={loading}
                  >
                    Salvar Perfil
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== TAB: GALERIA ========== */}
          <TabsContent value="galeria" className="space-y-4">
            {/* Header da Galeria */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-white">Minha Galeria</h2>
                <p className="text-gray-400 text-sm">
                  {(perfil?.fotos?.length || 0) + (perfil?.videos?.length || 0)} mídias
                </p>
              </div>
              <Button
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                onClick={() => setShowGaleriaDialog(true)}
              >
                <Upload className="h-4 w-4 mr-2" />
                Adicionar
              </Button>
            </div>

            {/* Fotos */}
            <Card className="bg-black/30 border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-white flex items-center gap-2 text-base">
                  <Image className="h-5 w-5 text-purple-400" />
                  Fotos
                  <Badge variant="outline" className="ml-2 text-gray-400">{perfil?.fotos?.length || 0}/10</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {perfil?.fotos?.length > 0 ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                    {perfil.fotos.map((foto) => {
                      const imageUrl = foto.url.startsWith('/uploads') 
                        ? `${process.env.REACT_APP_BACKEND_URL}${foto.url}`
                        : foto.url;
                      return (
                        <div key={foto.id} className="relative group aspect-square">
                          <img 
                            src={imageUrl} 
                            alt={foto.legenda || 'Foto'} 
                            className="w-full h-full object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                          />
                          <button
                            onClick={() => removerMidia('foto', foto.id)}
                            className="absolute top-1 right-1 bg-red-600/90 p-1.5 rounded-full sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="h-3 w-3 text-white" />
                          </button>
                          {foto.legenda && (
                            <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-1 truncate rounded-b-lg">
                              {foto.legenda}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Image className="h-12 w-12 text-gray-600 mx-auto mb-2" />
                    <p className="text-gray-500">Nenhuma foto ainda</p>
                    <Button
                      variant="outline"
                      className="mt-3 border-purple-500 text-purple-400 hover:bg-purple-500/20"
                      onClick={() => setShowGaleriaDialog(true)}
                    >
                      Adicionar primeira foto
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Vídeos */}
            <Card className="bg-black/30 border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-white flex items-center gap-2 text-base">
                  <Video className="h-5 w-5 text-pink-400" />
                  Vídeos
                  <Badge variant="outline" className="ml-2 text-gray-400">{perfil?.videos?.length || 0}/5</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {perfil?.videos?.length > 0 ? (
                  <div className="space-y-2">
                    {perfil.videos.map((video) => {
                      const videoUrl = video.url.startsWith('/uploads') 
                        ? `${process.env.REACT_APP_BACKEND_URL}${video.url}`
                        : video.url;
                      const isLocalVideo = video.url.startsWith('/uploads');
                      return (
                        <div key={video.id} className="flex items-center gap-3 bg-white/5 rounded-lg p-3 group">
                          <div className="flex-shrink-0 w-16 h-16 bg-pink-600/20 rounded-lg flex items-center justify-center">
                            <Video className="h-6 w-6 text-pink-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium truncate">{video.legenda || 'Vídeo'}</p>
                            {isLocalVideo ? (
                              <a 
                                href={videoUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-pink-400 text-sm hover:underline"
                              >
                                Ver vídeo
                              </a>
                            ) : (
                              <a 
                                href={video.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-pink-400 text-sm truncate block hover:underline"
                              >
                                {video.url}
                              </a>
                            )}
                          </div>
                          <button
                            onClick={() => removerMidia('video', video.id)}
                            className="text-red-500 p-2 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Video className="h-12 w-12 text-gray-600 mx-auto mb-2" />
                    <p className="text-gray-500">Nenhum vídeo ainda</p>
                    <Button
                      variant="outline"
                      className="mt-3 border-pink-500 text-pink-400 hover:bg-pink-500/20"
                      onClick={() => setShowGaleriaDialog(true)}
                    >
                      Adicionar primeiro vídeo
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== TAB: AMIGOS ========== */}
          <TabsContent value="amigos" className="space-y-6">
            {/* Convites Pendentes */}
            {convitesPendentes.length > 0 && (
              <Card className="bg-black/30 border-yellow-500/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Bell className="h-5 w-5 text-yellow-400" />
                    Convites Pendentes ({convitesPendentes.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {convitesPendentes.map((convite) => (
                    <div key={convite.convite_id} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                      <div 
                        className="flex items-center gap-3 cursor-pointer"
                        onClick={() => verPerfil(convite.aluno.id)}
                      >
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={convite.aluno.avatar_url} />
                          <AvatarFallback className="bg-purple-600 text-white">
                            {convite.aluno.nome?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-white font-medium">{convite.aluno.nome}</p>
                          <p className="text-gray-400 text-xs">Quer ser seu amigo</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => aceitarConvite(convite.convite_id)}
                        >
                          Aceitar
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="border-red-500 text-red-500 hover:bg-red-500/20"
                          onClick={() => recusarConvite(convite.convite_id)}
                        >
                          Recusar
                        </Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Meus Amigos */}
            <Card className="bg-black/30 border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-white flex items-center gap-2">
                  <Heart className="h-5 w-5 text-pink-400" />
                  Meus Amigos ({amigos.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {amigos.length === 0 ? (
                  <p className="text-gray-400 text-center py-4">Você ainda não tem amigos. Adicione colegas abaixo!</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {amigos.map((amigo) => (
                      <div 
                        key={amigo.id} 
                        className="bg-white/5 rounded-lg p-3 text-center cursor-pointer hover:bg-white/10 transition-colors"
                        onClick={() => verPerfil(amigo.id)}
                      >
                        <Avatar className="h-14 w-14 mx-auto mb-2">
                          <AvatarImage src={amigo.avatar_url} />
                          <AvatarFallback className="bg-purple-600 text-white">
                            {amigo.nome?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <p className="text-white text-sm font-medium truncate">{amigo.nome}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Encontrar Colegas */}
            <Card className="bg-black/30 border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-white flex items-center gap-2">
                  <Search className="h-5 w-5 text-blue-400" />
                  Encontrar Colegas
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    value={searchColega}
                    onChange={(e) => setSearchColega(e.target.value)}
                    placeholder="Buscar por nome..."
                    className="pl-10 bg-white/10 border-white/20 text-white"
                  />
                </div>
                
                {colegasFiltrados.length === 0 ? (
                  <p className="text-gray-400 text-center py-4">
                    {colegas.length === 0 ? "Nenhum colega encontrado na sua escola" : "Nenhum resultado para a busca"}
                  </p>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {colegasFiltrados.map((colega) => (
                      <div key={colega.id} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                        <div 
                          className="flex items-center gap-3 cursor-pointer"
                          onClick={() => verPerfil(colega.id)}
                        >
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={colega.avatar_url} />
                            <AvatarFallback className="bg-purple-600 text-white">
                              {colega.nome?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-white font-medium">{colega.nome}</span>
                        </div>
                        
                        {colega.amizade_status === "amigos" ? (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500">
                            <UserCheck className="h-3 w-3 mr-1" />
                            Amigo
                          </Badge>
                        ) : colega.amizade_status === "convite_enviado" ? (
                          <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500">
                            Enviado
                          </Badge>
                        ) : colega.amizade_status === "convite_recebido" ? (
                          <Button 
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              aceitarConvite(colega.amizade_id);
                            }}
                          >
                            Aceitar
                          </Button>
                        ) : (
                          <Button 
                            size="sm"
                            variant="outline"
                            className="border-purple-500 text-purple-400 hover:bg-purple-500/20"
                            onClick={(e) => {
                              e.stopPropagation();
                              enviarConvite(colega.id);
                            }}
                          >
                            <UserPlus className="h-4 w-4 mr-1" />
                            Adicionar
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== TAB: ARENA ========== */}
          <TabsContent value="arena" className="space-y-6">
            {votacaoAtual ? (
              <Card className="bg-black/30 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Vote className="h-5 w-5 text-purple-400" />
                      Votação: {votacaoAtual.turma_nome}
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-gray-400 hover:text-white"
                      onClick={() => setVotacaoAtual(null)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Treino: {votacaoAtual.data_treino} - Vote em cada categoria
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {votacaoAtual.categorias.map((catId) => {
                    const categoria = categorias.find(c => c.id === catId);
                    if (!categoria) return null;
                    const Icon = CATEGORIAS_ICONS[categoria.id] || Star;
                    
                    return (
                      <div key={catId} className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Icon className="h-5 w-5 text-purple-400" />
                          <span className="text-white font-semibold">{categoria.nome}</span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {alunosTurma.map((aluno) => (
                            <Button
                              key={aluno.id}
                              variant={votosAluno[catId] === aluno.id ? "default" : "outline"}
                              className={`h-auto py-2 ${
                                votosAluno[catId] === aluno.id 
                                  ? 'bg-purple-600 hover:bg-purple-700 border-purple-600' 
                                  : 'border-white/20 text-white hover:bg-white/10'
                              }`}
                              onClick={() => setVotosAluno(prev => ({...prev, [catId]: aluno.id}))}
                            >
                              {aluno.nome}
                            </Button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  
                  <Button 
                    onClick={registrarVoto}
                    disabled={loading || Object.keys(votosAluno).length !== votacaoAtual.categorias.length}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                  >
                    {loading ? "Enviando..." : "Confirmar Votos"}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Ranking */}
                <Card className="bg-black/30 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-yellow-400" />
                      Seu Desempenho
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-lg p-4 text-center">
                        <Trophy className="h-8 w-8 text-yellow-400 mx-auto mb-2" />
                        <p className="text-3xl font-bold text-white">{estatisticas?.pontos || 0}</p>
                        <p className="text-gray-400 text-sm">Pontos Totais</p>
                      </div>
                      <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-lg p-4 text-center">
                        <Star className="h-8 w-8 text-purple-400 mx-auto mb-2" />
                        <p className="text-3xl font-bold text-white">{estatisticas?.votos_recebidos || 0}</p>
                        <p className="text-gray-400 text-sm">Votos Recebidos</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Votações Disponíveis */}
                <Card className="bg-black/30 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Vote className="h-5 w-5 text-purple-400" />
                      Votações Disponíveis
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {votacoes.length === 0 ? (
                      <div className="text-center py-8">
                        <Vote className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                        <p className="text-gray-400">Nenhuma votação disponível no momento</p>
                        <p className="text-gray-500 text-sm mt-1">Aguarde seu professor abrir uma votação</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {votacoes.map((sessao) => (
                          <div 
                            key={sessao.id}
                            className="bg-white/5 rounded-lg p-4 hover:bg-white/10 transition-colors cursor-pointer"
                            onClick={() => iniciarVotacao(sessao)}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-white font-semibold">{sessao.turma_nome}</p>
                                <p className="text-gray-400 text-sm">Treino: {sessao.data_treino}</p>
                                <p className="text-purple-400 text-xs mt-1">
                                  {sessao.categorias?.length || 0} categorias para votar
                                </p>
                              </div>
                              <Button className="bg-purple-600 hover:bg-purple-700">
                                Votar
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Dialog: Escolher Avatar */}
      <Dialog open={showAvatarDialog} onOpenChange={setShowAvatarDialog}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Escolha seu Avatar</DialogTitle>
            <DialogDescription className="text-gray-400">
              Selecione um avatar para seu perfil
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-4 gap-3 py-4 max-h-64 overflow-y-auto">
            {avatares.map((avatar) => (
              <div
                key={avatar.id}
                onClick={() => setSelectedAvatar(avatar.id)}
                className={`cursor-pointer rounded-lg p-2 transition-all ${
                  selectedAvatar === avatar.id 
                    ? 'bg-purple-600 ring-2 ring-purple-400' 
                    : 'bg-white/10 hover:bg-white/20'
                }`}
              >
                <Avatar className="h-12 w-12 mx-auto">
                  <AvatarImage src={avatar.url} />
                  <AvatarFallback>?</AvatarFallback>
                </Avatar>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAvatarDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={salvarPerfil}
              disabled={!selectedAvatar || loading}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Salvar Avatar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Ver Perfil de Outro Aluno */}
      <Dialog open={showPerfilDialog} onOpenChange={setShowPerfilDialog}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-sm max-h-[80vh] overflow-y-auto">
          {perfilOutro && (
            <>
              <div className="flex flex-col items-center text-center py-4">
                <Avatar className="h-20 w-20 border-4 border-purple-400 mb-4">
                  <AvatarImage src={perfilOutro.perfil?.avatar_url} />
                  <AvatarFallback className="bg-purple-600 text-white text-2xl">
                    {perfilOutro.aluno?.nome?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <h2 className="text-xl font-bold">{perfilOutro.aluno?.nome}</h2>
                {perfilOutro.perfil?.posicao && (
                  <Badge className="mt-1 bg-green-600">
                    {POSICOES_FUTEBOL.find(p => p.id === perfilOutro.perfil.posicao)?.nome || perfilOutro.perfil.posicao}
                  </Badge>
                )}
                {perfilOutro.perfil?.bio && (
                  <p className="text-gray-400 mt-2 italic">"{perfilOutro.perfil.bio}"</p>
                )}
              </div>

              {/* Informações de Atleta */}
              <div className="grid grid-cols-4 gap-2 text-center py-2 border-t border-white/10">
                {perfilOutro.perfil?.numero_camisa && (
                  <div>
                    <p className="text-lg font-bold text-purple-400">#{perfilOutro.perfil.numero_camisa}</p>
                    <p className="text-xs text-gray-500">Camisa</p>
                  </div>
                )}
                {perfilOutro.perfil?.pe_dominante && (
                  <div>
                    <p className="text-sm text-white">{perfilOutro.perfil.pe_dominante}</p>
                    <p className="text-xs text-gray-500">Pé</p>
                  </div>
                )}
                {perfilOutro.perfil?.altura && (
                  <div>
                    <p className="text-sm text-white">{perfilOutro.perfil.altura}m</p>
                    <p className="text-xs text-gray-500">Altura</p>
                  </div>
                )}
                {perfilOutro.perfil?.peso && (
                  <div>
                    <p className="text-sm text-white">{perfilOutro.perfil.peso}kg</p>
                    <p className="text-xs text-gray-500">Peso</p>
                  </div>
                )}
              </div>

              {/* Estatísticas */}
              <div className="grid grid-cols-3 gap-4 py-4 border-t border-white/10">
                <div className="text-center">
                  <p className="text-xl font-bold">{perfilOutro.estatisticas?.pontos || 0}</p>
                  <p className="text-xs text-gray-400">Pontos</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold">{perfilOutro.estatisticas?.vitorias || 0}</p>
                  <p className="text-xs text-gray-400">Vitórias</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold">{perfilOutro.estatisticas?.votos_recebidos || 0}</p>
                  <p className="text-xs text-gray-400">Votos</p>
                </div>
              </div>

              {/* Galeria do outro aluno */}
              {(perfilOutro.perfil?.fotos?.length > 0 || perfilOutro.perfil?.videos?.length > 0) && (
                <div className="border-t border-white/10 pt-3">
                  <p className="text-gray-400 text-sm mb-2">Galeria</p>
                  {perfilOutro.perfil?.fotos?.length > 0 && (
                    <div className="grid grid-cols-3 gap-1 mb-2">
                      {perfilOutro.perfil.fotos.slice(0, 6).map((foto) => (
                        <img 
                          key={foto.id}
                          src={foto.url} 
                          alt={foto.legenda || ''} 
                          className="w-full aspect-square object-cover rounded"
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              <DialogFooter className="border-t border-white/10 pt-4">
                {perfilOutro.amizade_status === "amigos" ? (
                  <Button 
                    variant="outline"
                    className="w-full border-red-500 text-red-500 hover:bg-red-500/20"
                    onClick={() => {
                      desfazerAmizade(perfilOutro.amizade_id);
                      setShowPerfilDialog(false);
                    }}
                  >
                    <UserX className="h-4 w-4 mr-2" />
                    Desfazer Amizade
                  </Button>
                ) : perfilOutro.amizade_status === "convite_enviado" ? (
                  <Button disabled className="w-full bg-gray-600">
                    Convite Enviado
                  </Button>
                ) : perfilOutro.amizade_status === "convite_recebido" ? (
                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={() => {
                      aceitarConvite(perfilOutro.amizade_id);
                      setShowPerfilDialog(false);
                    }}
                  >
                    <UserCheck className="h-4 w-4 mr-2" />
                    Aceitar Convite
                  </Button>
                ) : (
                  <Button 
                    className="w-full bg-purple-600 hover:bg-purple-700"
                    onClick={() => {
                      enviarConvite(perfilOutro.aluno.id);
                      setShowPerfilDialog(false);
                    }}
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Adicionar Amigo
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Adicionar Mídia */}
      <Dialog open={showGaleriaDialog} onOpenChange={setShowGaleriaDialog}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Adicionar à Galeria</DialogTitle>
            <DialogDescription className="text-gray-400">
              Envie do seu dispositivo ou cole um link
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* ===== UPLOAD DE FOTO ===== */}
            <div className="p-4 bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-xl border border-purple-500/30">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-purple-400">
                  <Image className="h-5 w-5" />
                  <span className="font-semibold">Fotos</span>
                </div>
                <span className="text-gray-400 text-sm">{perfil?.fotos?.length || 0}/10</span>
              </div>
              
              {/* Botão de Upload */}
              <label className={`flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-purple-500/50 rounded-lg cursor-pointer hover:bg-purple-500/10 transition-colors ${uploadingFoto ? 'opacity-50' : ''}`}>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileUpload(e, 'foto')}
                  disabled={uploadingFoto || (perfil?.fotos?.length || 0) >= 10}
                />
                {uploadingFoto ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-400"></div>
                    <span className="text-purple-300">Enviando...</span>
                  </div>
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-purple-400 mb-1" />
                    <span className="text-purple-300 text-sm">Toque para escolher foto</span>
                    <span className="text-gray-500 text-xs">Máx. 5MB</span>
                  </>
                )}
              </label>
              
              {/* Ou por Link */}
              <div className="mt-3 pt-3 border-t border-white/10">
                <div className="flex items-center gap-2 text-gray-400 text-xs mb-2">
                  <Link className="h-3 w-3" />
                  <span>Ou cole um link</span>
                </div>
                <div className="flex gap-2">
                  <Input
                    value={novaFotoUrl}
                    onChange={(e) => setNovaFotoUrl(e.target.value)}
                    placeholder="https://..."
                    className="bg-white/10 border-white/20 text-white text-sm h-9"
                  />
                  <Button 
                    onClick={adicionarFoto}
                    disabled={!novaFotoUrl || (perfil?.fotos?.length || 0) >= 10}
                    size="sm"
                    className="bg-purple-600 hover:bg-purple-700 px-3"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* ===== UPLOAD DE VÍDEO ===== */}
            <div className="p-4 bg-gradient-to-r from-pink-600/20 to-orange-600/20 rounded-xl border border-pink-500/30">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-pink-400">
                  <Video className="h-5 w-5" />
                  <span className="font-semibold">Vídeos</span>
                </div>
                <span className="text-gray-400 text-sm">{perfil?.videos?.length || 0}/5</span>
              </div>
              
              {/* Botão de Upload */}
              <label className={`flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-pink-500/50 rounded-lg cursor-pointer hover:bg-pink-500/10 transition-colors ${uploadingVideo ? 'opacity-50' : ''}`}>
                <input
                  type="file"
                  accept="video/*"
                  className="hidden"
                  onChange={(e) => handleFileUpload(e, 'video')}
                  disabled={uploadingVideo || (perfil?.videos?.length || 0) >= 5}
                />
                {uploadingVideo ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-pink-400"></div>
                    <span className="text-pink-300">Enviando...</span>
                  </div>
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-pink-400 mb-1" />
                    <span className="text-pink-300 text-sm">Toque para escolher vídeo</span>
                    <span className="text-gray-500 text-xs">Máx. 50MB</span>
                  </>
                )}
              </label>
              
              {/* Ou por Link */}
              <div className="mt-3 pt-3 border-t border-white/10">
                <div className="flex items-center gap-2 text-gray-400 text-xs mb-2">
                  <Link className="h-3 w-3" />
                  <span>Ou cole um link (YouTube, etc.)</span>
                </div>
                <div className="flex gap-2">
                  <Input
                    value={novoVideoUrl}
                    onChange={(e) => setNovoVideoUrl(e.target.value)}
                    placeholder="https://..."
                    className="bg-white/10 border-white/20 text-white text-sm h-9"
                  />
                  <Button 
                    onClick={adicionarVideo}
                    disabled={!novoVideoUrl || (perfil?.videos?.length || 0) >= 5}
                    size="sm"
                    className="bg-pink-600 hover:bg-pink-700 px-3"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGaleriaDialog(false)} className="w-full sm:w-auto">
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
